import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable } from "rxjs";
import { SURVEY_URL } from "../shared/src";
import { ErrorHandlerService } from "./error-handler.service";

@Injectable({
    providedIn: 'root',
})

export class SelloutDashboardService {
    private readonly SELLOUT_DASHBOARD_API = `${SURVEY_URL}/sellOutDashboard`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    /**
     * Refreshes the backend materialized views for the dashboard
     */
    public refreshData(): Observable<any> {
        return this.http
            .post<any>(`${this.SELLOUT_DASHBOARD_API}/refresh-data`, {})
            .pipe(catchError((error) => this.errorHandler.handleError(error)));
    }

    public getLastUpdated(): Observable<any> {
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/last-updated`)
            .pipe(catchError((error) => this.errorHandler.handleError(error)));
    }

    public getSubcategoriesByCategoryIds(categoryIds: number[], countryId?: number): Observable<any> {
        let params = new HttpParams().set('categoryIds', categoryIds.join(','));
        if (countryId) params = params.set('countryId', countryId.toString());
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/subcategories-by-category`, { params })
            .pipe(catchError((error) => this.errorHandler.handleError(error)));
    }

    // --- User Access ---

    /** Returns the current user's allowed channelIds and areaGroupIds from the JWT token. */
    public getMyAccess(): Observable<{ channelIds: number[] | null; areaGroupIds: number[] | null }> {
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/my-access`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    // --- Overview ---


    public getScorecards(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/scorecards`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getTdsSummary(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/tds-summary`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getMonthlyTrend(filters: any): Observable<any> {
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/monthly-trend`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getCategoryMix(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/category-mix`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getDailyTrend(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/daily-trend`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAccountPerformance(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/account-performance`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getStatePerformance(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/state-performance`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAreaPerformance(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/area-performance`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    // --- Promoter Rankings ---

    public getRankingsScorecards(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/rankings/scorecards`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getTopPerformers(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/rankings/top-performers`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getNeedsAttention(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/rankings/needs-attention`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getFullTeamData(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/rankings/full-team`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    // --- Product Performance ---

    public getProductScorecards(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/scorecards`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getBundleModels(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/bundle-models`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getBundleModelTrend(filters: any, modelcode: string): Observable<any[]> {
        const params = new HttpParams({ fromObject: { ...filters, modelcode } });
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/bundle-model-trend`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getProductCategoryData(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/category-data`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getStarVsNonStarData(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/star-vs-nonstar`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getBundleData(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/bundle-data`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getSubcategoryRows(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/subcategory-rows`, { params: filters, })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getProductRows(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/products/product-rows`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }



    // --- Store & Account ---

    public getAccounts(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/accounts`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAccountHeader(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/accounts/header`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getStoreAccountScorecards(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/store-account/scorecards`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAccountRankings(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/store-account/rankings`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getTopStores(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/store-account/top-stores`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAccountStores(filters: any): Observable<any> {
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/store-account/account-stores`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getMonthlyStoreSalesRanking(filters: {
        countryId: number;
        year: number;
        month: number;
        months?: number;
        brandId?: number | string;
        measure?: 'value' | 'quantity';
        limit?: number;
        [key: string]: any;
    }): Observable<any> {
        return this.http
            .get<any>(`${this.SELLOUT_DASHBOARD_API}/store-account/monthly-ranking`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getStorePerformanceChart(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/store-account/performance-chart`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getStores(filters: any): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/stores`, { params: filters })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getSelloutModels(params: {
        countryId?: number;
        categoryId?: number;
        subCategoryId?: number;
        searchFilter?: string;
    }): Observable<any[]> {
        let queryParams = new HttpParams();
        if (params.countryId) {
            queryParams = queryParams.set('countryId', params.countryId);
        }
        if (params.categoryId) {
            queryParams = queryParams.set('categoryId', params.categoryId);
        }
        if (params.subCategoryId) {
            queryParams = queryParams.set('subCategoryId', params.subCategoryId);
        }
        if (params.searchFilter) {
            queryParams = queryParams.set('searchFilter', params.searchFilter);
        }
        return this.http
            .get<any[]>(`${this.SELLOUT_DASHBOARD_API}/models`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }
}
